// Live Map of today's trucks and job sites, with a side pane of jobs for
// "statusing" (are all the trucks there and checked in?) and route context.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ChevronDoubleLeftIcon,
  MapIcon,
  QueueListIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import clsx from 'clsx';

import { groupBy, uniq, uniqBy } from 'lodash';

import { getIsJobFiltered } from '@isd/lib/helpers/scheduler';

import { useAuthContext } from '@/context/AuthContext';
import { useSchedulerContext } from '@/context/SchedulerContext';

import { useScheduledJobs } from '@/components/Job/useScheduledJobs';
import LiveMapJobsPane, {
  PinnedSummary,
} from '@/components/LiveMap/LiveMapJobsPane';
import {
  SITE_PIN_HEIGHT_PX,
  SITE_ROLE_LABELS,
  SITE_TAG_HEIGHT_PX,
  SiteMarker,
  SiteMarkerData,
  SiteRoleIcon,
  TRUCK_MARKER_SIZE_PX,
  TruckMarker,
} from '@/components/LiveMap/LiveMapMarkers';
import {
  Circle,
  GoogleMap,
  InfoWindow,
  Polygon,
  Polyline,
  useGoogleMap,
} from '@/components/LiveMap/MapPrimitives';
import SiteDetails from '@/components/LiveMap/SiteDetails';
import TruckDetails from '@/components/LiveMap/TruckDetails';
import { LatLngLiteral } from '@/components/LiveMap/geo';
import {
  toLiveMapJob,
  toLiveMapOperators,
} from '@/components/LiveMap/liveMapAdapters';
import {
  LiveMapJob,
  LiveMapOperator,
  LiveMapSite,
  OPERATOR_MARKER_COLORS,
  findLastVisitedSite,
  getActiveAssignments,
  getDayKind,
  getIsActiveAssignmentStatus,
  getIsLocationStale,
  getSiteTrips,
  getTruckActivity,
  mergePaths,
} from '@/components/LiveMap/liveMapModel';
import {
  useFirestoreTruckLocations,
  useFirestoreTruckTrail,
  useJobsAssignments,
  useOperatorProfiles,
  useTruckHistories,
} from '@/components/LiveMap/useLiveMapData';
import {
  RECENT_JOBS_DAYS,
  RECENT_JOBS_LIMIT,
  useRecentJobs,
} from '@/components/LiveMap/useRecentJobs';
import {
  getIsSimulatorAvailable,
  useSimulatedTrucks,
} from '@/components/LiveMap/useSimulatedTrucks';

// Re-render on this cadence so "x min ago" and staleness stay current
const NOW_REFRESH_MS = 15000;

/**
 * Current time, refreshed on an interval.
 * @param {number} intervalMs
 * @returns {number} Epoch milliseconds.
 */
function useNow(intervalMs: number): number {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), intervalMs);
    return () => clearInterval(interval);
  }, [intervalMs]);
  return nowMs;
}

/**
 * The jobs using a site, each with the site as that job sees it.
 * @param {LiveMapJob[]} jobs
 * @param {string} siteId
 * @returns {Array<{ job: LiveMapJob, site: LiveMapSite }>}
 */
function getSiteEntries(
  jobs: LiveMapJob[],
  siteId: string
): Array<{ job: LiveMapJob; site: LiveMapSite }> {
  return jobs.flatMap((job) => {
    const site = job.sites.find((jobSite) => jobSite.id === siteId);
    return site ? [{ job, site }] : [];
  });
}

// Neutral color for last week's jobs shown as context on future days
const CONTEXT_JOB_COLOR = '#777777';

/**
 * Map for the scheduler's current day. Today it shows live trucks; past days
 * show the routes trucks drove (from GPS history) for the pinned jobs; future
 * days show the planned jobs plus the sites used over the last week.
 * @returns {React.JSX.Element}
 */
export default function LiveMapView() {
  const nowMs = useNow(NOW_REFRESH_MS);
  const { userDoc } = useAuthContext();
  const {
    schedulerCurrentDate,
    selectedDispatchSchedule,
    activeFilters,
    brokerCompanies,
    setSchedulerView,
    pinnedJobIds: allPinnedJobIds,
    togglePinnedJob,
    setJobsPinned,
    clearPinnedJobs,
    isLoadingDispatchScheduleOptions,
  } = useSchedulerContext();
  const dayKind = getDayKind(schedulerCurrentDate);
  const isLiveDay = dayKind === 'today';
  const dayLabel = schedulerCurrentDate.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const { jobs: jobDocs, isLoading: isLoadingJobs } = useScheduledJobs();
  const jobs = useMemo(() => jobDocs.map(toLiveMapJob), [jobDocs]);
  const jobsById = useMemo(
    () => Object.fromEntries(jobs.map((job) => [job.id, job])),
    [jobs]
  );
  const jobIds = useMemo(() => jobs.map((job) => job.id), [jobs]);
  // Pinned jobs shown on this map; pins hidden by a filter don't count
  const pinnedJobIds = useMemo(
    () => allPinnedJobIds.filter((jobId) => !!jobsById[jobId]),
    [allPinnedJobIds, jobsById]
  );

  const { assignments, isLoading: isLoadingAssignments } =
    useJobsAssignments(jobIds);
  const realOperators = useMemo(
    () => toLiveMapOperators(assignments),
    [assignments]
  );

  const isSimulatorAvailable = getIsSimulatorAvailable();
  const [isSimulationOn, setIsSimulationOn] = useState(false);
  // Live positions (real or simulated) only exist for today
  const isSimulating = isSimulationOn && isLiveDay;
  const simulation = useSimulatedTrucks(jobs, realOperators, isSimulating);

  const operators = useMemo(
    () => [...realOperators, ...simulation.standInOperators],
    [realOperators, simulation.standInOperators]
  );
  // Operators expected on the road today: accepted or checked in on a job
  const activeOperators = useMemo(
    () =>
      operators.filter((operator) => getActiveAssignments(operator).length > 0),
    [operators]
  );
  const liveLocations = useFirestoreTruckLocations(
    activeOperators.map((operator) => operator.uid),
    isLiveDay && !isSimulating
  );
  const locations = isSimulating ? simulation.locations : liveLocations;

  // On phones the pane covers the map, so start with it closed there
  const [isPaneOpen, setIsPaneOpen] = useState(
    () => typeof window === 'undefined' || window.innerWidth >= 768
  );
  const [selectedTruckUid, setSelectedTruckUid] = useState<string | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  const visibleJobs = useMemo(
    () =>
      pinnedJobIds.length
        ? jobs.filter((job) => pinnedJobIds.includes(job.id))
        : jobs,
    [jobs, pinnedJobIds]
  );
  const visibleJobIds = useMemo(
    () => new Set(visibleJobs.map((job) => job.id)),
    [visibleJobs]
  );

  // Trucks working a visible job, colored by the first visible job they're on.
  // A truck whose phone stopped reporting is only drawn while it is checked in
  // (a dispatcher needs to see that); accepted trucks that haven't started
  // reporting today would just clutter the map with old positions.
  const visibleTrucks = useMemo(
    () =>
      activeOperators.flatMap((operator) => {
        const location = locations[operator.uid];
        const assignment = getActiveAssignments(operator).find((a) =>
          visibleJobIds.has(a.jobId)
        );
        const job = assignment ? jobsById[assignment.jobId] : undefined;
        if (!location || !assignment || !job) return [];
        const isStale = getIsLocationStale(location, nowMs);
        return isStale && assignment.status !== 'checkedIn'
          ? []
          : [{ operator, location, job }];
      }),
    [activeOperators, locations, visibleJobIds, jobsById, nowMs]
  );

  // Avatars and location-sharing switch for the trucks drawn on the map
  const profileUids = useMemo(
    () =>
      visibleTrucks
        .filter(({ operator }) => !operator.isSimulated)
        .map(({ operator }) => operator.uid),
    [visibleTrucks]
  );
  const profiles = useOperatorProfiles(profileUids);

  // Grey react-app pins normally; colored tags with site names once jobs are
  // pinned, to give the focused jobs more context
  const siteMarkerVariant = pinnedJobIds.length ? 'tag' : 'pin';

  const selectedTruck =
    visibleTrucks.find((truck) => truck.operator.uid === selectedTruckUid) ||
    null;
  useEffect(() => {
    if (selectedTruckUid && !selectedTruck) setSelectedTruckUid(null);
  }, [selectedTruckUid, !!selectedTruck]);

  const selectedTruckJobs = useMemo(
    () =>
      selectedTruck
        ? getActiveAssignments(selectedTruck.operator).flatMap((assignment) =>
            jobsById[assignment.jobId]
              ? [jobsById[assignment.jobId] as LiveMapJob]
              : []
          )
        : [],
    [selectedTruck?.operator, jobsById]
  );

  const firestoreTrail = useFirestoreTruckTrail(
    selectedTruck?.operator.uid || null,
    !isSimulating && !selectedTruck?.operator.isSimulated
  );
  const selectedTrail = selectedTruck
    ? isSimulating
      ? simulation.trails[selectedTruck.operator.uid] || []
      : firestoreTrail
    : [];
  const selectedTrailPath = useMemo(
    () => selectedTrail.map(({ lat, lng }) => ({ lat, lng })),
    [selectedTrail]
  );

  const selectedActivity = selectedTruck
    ? getTruckActivity(
        selectedTruck.location,
        selectedTruckJobs,
        selectedTrail.length ? selectedTrail : undefined
      )
    : null;
  const cameFrom = selectedTruck
    ? findLastVisitedSite(
        selectedTrail.length ? selectedTrail : selectedTruck.location.recent,
        selectedTruckJobs
      )
    : null;

  // Group operators per job (every status, for the pane's statusing)
  const operatorsByJobId = useMemo(() => {
    const pairs = operators.flatMap((operator) =>
      uniq(operator.assignments.map((assignment) => assignment.jobId)).map(
        (jobId) => ({ jobId, operator })
      )
    );
    const grouped = groupBy(pairs, 'jobId');
    return Object.fromEntries(
      Object.entries(grouped).map(([jobId, entries]) => [
        jobId,
        entries.map((entry) => entry.operator),
      ])
    ) as Record<string, LiveMapOperator[]>;
  }, [operators]);

  // Pinned jobs whose trucks' GPS history is needed: on past days every
  // pinned job (to draw the routes driven), today only jobs with more than two
  // sites (whose lanes come from the legs trucks actually drove)
  const routeDrivers = useMemo(
    () =>
      jobs
        .filter(
          (job) =>
            pinnedJobIds.includes(job.id) &&
            (dayKind === 'past' || (isLiveDay && job.sites.length > 2))
        )
        .map((job) => ({
          job,
          // Everyone who drove (or is driving) the job today, finished trucks included
          operators: (operatorsByJobId[job.id] || []).filter((operator) =>
            operator.assignments.some(
              (assignment) =>
                assignment.jobId === job.id &&
                (getIsActiveAssignmentStatus(assignment.status) ||
                  assignment.status === 'checkedOut')
            )
          ),
        })),
    [jobs, pinnedJobIds, operatorsByJobId, dayKind, isLiveDay]
  );
  const historyUids = useMemo(
    () =>
      isSimulating
        ? []
        : uniq(
            routeDrivers.flatMap(({ operators }) =>
              operators
                .filter((operator) => !operator.isSimulated)
                .map((operator) => operator.uid)
            )
          ),
    [routeDrivers, isSimulating]
  );
  const histories = useTruckHistories(historyUids, schedulerCurrentDate);
  const observedLanes = useMemo(
    () =>
      routeDrivers
        .filter(({ job }) => job.sites.length > 2)
        .flatMap(({ job, operators }) => {
          const paths = operators.map((operator) =>
            isSimulating
              ? simulation.trails[operator.uid] || []
              : mergePaths(
                  histories[operator.uid] || [],
                  locations[operator.uid]?.recent || []
                )
          );
          const sitesById = Object.fromEntries(
            job.sites.map((site) => [site.id, site])
          );
          return getSiteTrips(paths, job.sites).flatMap(
            ({ fromSiteId, toSiteId, count }) => {
              const from = sitesById[fromSiteId];
              const to = sitesById[toSiteId];
              return from && to ? [{ job, from, to, count }] : [];
            }
          );
        }),
    [routeDrivers, isSimulating, simulation.trails, histories, locations]
  );
  // Past days: the route each truck of the pinned jobs drove that day
  const pastRoutes = useMemo(
    () =>
      dayKind !== 'past'
        ? []
        : uniqBy(
            routeDrivers.flatMap(({ job, operators }) =>
              operators.map((operator) => ({ job, operator }))
            ),
            ({ operator }) => operator.uid
          ).flatMap(({ job, operator }) => {
            const path = (histories[operator.uid] || []).map(
              ({ lat, lng }) => ({ lat, lng })
            );
            return path.length > 1 ? [{ job, operator, path }] : [];
          }),
    [dayKind, routeDrivers, histories]
  );

  // Future days: last week's jobs, for the sites trucks have been using
  const recentJobs = useRecentJobs({
    userDoc,
    dispatchScheduleDoc: selectedDispatchSchedule.dispatchScheduleDoc,
    // Wait for the saved or URL schedule, which starts out as "All Jobs"
    enabled: dayKind === 'future' && !isLoadingDispatchScheduleOptions,
  });
  const contextJobs = useMemo(
    () =>
      recentJobs.jobs
        .filter((jobDoc) =>
          getIsJobFiltered(jobDoc, activeFilters, { brokerCompanies })
        )
        .map((jobDoc) => ({
          ...toLiveMapJob(jobDoc, 0),
          color: CONTEXT_JOB_COLOR,
        })),
    [recentJobs.jobs, activeFilters, brokerCompanies]
  );

  // One marker per site, even when several visible jobs share it
  const siteMarkers = useMemo<SiteMarkerData[]>(() => {
    const bySiteId: Record<string, SiteMarkerData> = {};
    const markerFor = (site: LiveMapSite) =>
      (bySiteId[site.id] = bySiteId[site.id] || {
        site,
        entries: [],
        contextEntries: [],
      });
    visibleJobs.forEach((job) =>
      job.sites.forEach((site) => markerFor(site).entries.push({ job, site }))
    );
    // Last week's sites are context only while no job is pinned
    if (!pinnedJobIds.length) {
      contextJobs.forEach((job) =>
        job.sites.forEach((site) =>
          markerFor(site).contextEntries.push({ job, site })
        )
      );
    }
    return Object.values(bySiteId);
  }, [visibleJobs, contextJobs, pinnedJobIds.length]);

  const selectedSite =
    siteMarkers.find(({ site }) => site.id === selectedSiteId) || null;
  // The site popup lists every job of the day at the site, not only the ones on
  // the map, so more jobs can be pinned from it one after another
  const selectedSiteAllJobs = useMemo<SiteMarkerData | null>(
    () =>
      selectedSite
        ? {
            site: selectedSite.site,
            entries: getSiteEntries(jobs, selectedSite.site.id),
            contextEntries: getSiteEntries(contextJobs, selectedSite.site.id),
          }
        : null,
    [selectedSite, jobs, contextJobs]
  );
  useEffect(() => {
    if (selectedSiteId && !selectedSite) setSelectedSiteId(null);
  }, [selectedSiteId, !!selectedSite]);

  // Geofences are drawn for the sites of the pinned jobs
  const geofenceSites = useMemo(
    () =>
      uniqBy(
        jobs
          .filter((job) => pinnedJobIds.includes(job.id))
          .flatMap((job) => job.sites),
        'id'
      ),
    [jobs, pinnedJobIds]
  );

  const trucksOnSiteBySiteId = useMemo(() => {
    const counts: Record<string, number> = {};
    visibleTrucks.forEach(({ operator, location }) => {
      if (getIsLocationStale(location, nowMs)) return;
      const jobsOfTruck = getActiveAssignments(operator).flatMap((a) =>
        jobsById[a.jobId] ? [jobsById[a.jobId] as LiveMapJob] : []
      );
      const activity = getTruckActivity(location, jobsOfTruck);
      if (activity.kind === 'atSite') {
        counts[activity.site.id] = (counts[activity.site.id] || 0) + 1;
      }
    });
    return counts;
  }, [visibleTrucks, jobsById, nowMs]);

  const onClearTruck = useCallback(() => setSelectedTruckUid(null), []);
  const onClearSite = useCallback(() => setSelectedSiteId(null), []);
  // Clicking an empty spot of the map closes whichever popup is open
  const onClickMap = useCallback(() => {
    setSelectedTruckUid(null);
    setSelectedSiteId(null);
  }, []);
  const onSelectTruck = useCallback((uid: string) => {
    setSelectedSiteId(null);
    setSelectedTruckUid(uid);
  }, []);
  const onSelectSite = useCallback((siteId: string) => {
    setSelectedTruckUid(null);
    setSelectedSiteId(siteId);
  }, []);
  // Selecting a truck of a job that isn't pinned (while others are) pins the
  // job, so the truck shows on the map
  const onSelectTruckFromPane = (uid: string, jobId: string) => {
    if (pinnedJobIds.length && !pinnedJobIds.includes(jobId)) {
      togglePinnedJob(jobId);
    }
    setSelectedSiteId(null);
    setSelectedTruckUid(uid);
  };

  // Points the map should frame: visible sites plus visible trucks
  const framePoints = useMemo<LatLngLiteral[]>(
    () => [
      // Frame the day's jobs; last week's sites only when the day has none
      ...(siteMarkers.some(({ entries }) => entries.length)
        ? siteMarkers.filter(({ entries }) => entries.length)
        : siteMarkers
      ).map(({ site }) => site),
      ...visibleTrucks
        .filter(({ location }) => !getIsLocationStale(location, nowMs))
        .map(({ location }) => location.latest),
    ],
    [siteMarkers, visibleTrucks, nowMs]
  );
  // The map frames the jobs once, the first time there's something to show, and
  // again whenever a job is pinned. It never moves on GPS updates or when
  // switching days, so the dispatcher keeps the area they were looking at.
  // Unpinning a job only zooms out one level around the center.
  const [frameRequest, setFrameRequest] = useState(0);
  const [zoomOutRequest, setZoomOutRequest] = useState(0);
  const hasFramedOnceRef = useRef(false);
  const hasFramePoints = framePoints.length > 0;
  useEffect(() => {
    if (hasFramePoints && !hasFramedOnceRef.current) {
      hasFramedOnceRef.current = true;
      setFrameRequest((count) => count + 1);
    }
  }, [hasFramePoints]);
  const dayKey = schedulerCurrentDate.toDateString();
  const previousPinsRef = useRef({ dayKey, jobIds: [] as string[] });
  useEffect(() => {
    const previous = previousPinsRef.current;
    previousPinsRef.current = { dayKey, jobIds: pinnedJobIds };
    // Pins cleared by switching days weren't unpinned by the dispatcher
    if (previous.dayKey !== dayKey) return;
    // Nor were jobs dropped because a filter hides them now
    const unpinnedByUser = previous.jobIds.filter(
      (jobId) => !pinnedJobIds.includes(jobId) && !!jobsById[jobId]
    );
    if (pinnedJobIds.some((jobId) => !previous.jobIds.includes(jobId))) {
      setFrameRequest((count) => count + 1);
    } else if (unpinnedByUser.length) {
      setZoomOutRequest((count) => count + 1);
    }
    // jobsById only changes alongside pinnedJobIds (derived from it), so listing
    // it adds no extra runs
  }, [pinnedJobIds, dayKey, jobsById]);
  const frameKey = String(frameRequest);

  const isLoading = isLoadingJobs || isLoadingAssignments;
  const liveTruckCount = visibleTrucks.filter(
    ({ location }) => !getIsLocationStale(location, nowMs)
  ).length;

  return (
    <div className="live-map-view border-base-200 bg-base-100 flex h-full flex-col overflow-hidden rounded-xl border">
      <MapHeader
        title={isLiveDay ? `Live map · ${dayLabel}` : `Map · ${dayLabel}`}
        onClose={() => setSchedulerView('list')}
      />
      <div className="relative flex min-h-0 flex-1">
        <aside
          className={clsx(
            'bg-base-150 border-base-200 absolute inset-y-0 left-0 z-10 w-80 border-r shadow-lg transition-transform md:static md:shadow-none xl:w-96',
            isPaneOpen ? 'translate-x-0' : '-translate-x-full md:hidden'
          )}
          aria-label={isLiveDay ? "Today's jobs" : `Jobs on ${dayLabel}`}
        >
          <button
            type="button"
            className="btn btn-ghost btn-xs btn-square absolute top-2 right-2 z-1"
            onClick={() => setIsPaneOpen(false)}
            title="Hide jobs"
            aria-label="Hide jobs"
          >
            <ChevronDoubleLeftIcon className="size-4" />
          </button>
          <LiveMapJobsPane
            title={isLiveDay ? "Today's jobs" : `Jobs on ${dayLabel}`}
            jobs={jobs}
            operatorsByJobId={operatorsByJobId}
            locations={locations}
            pinnedJobIds={pinnedJobIds}
            highlightedJobIds={selectedTruckJobs.map((job) => job.id)}
            selectedTruckUid={selectedTruckUid}
            cameFromSiteId={cameFrom?.site.id || null}
            isLoading={isLoading}
            nowMs={nowMs}
            onTogglePin={togglePinnedJob}
            onShowAll={clearPinnedJobs}
            onSelectTruck={onSelectTruckFromPane}
          />
        </aside>

        <div className="relative min-w-0 flex-1">
          <GoogleMap onClick={onClickMap}>
            <FitBounds points={framePoints} frameKey={frameKey} />
            <ZoomOutOneLevel request={zoomOutRequest} />
            <PanToSelectedTruck
              position={selectedTruck?.location.latest || null}
              uid={selectedTruckUid}
            />

            {/* Lanes. A two-site job always runs A -> B. With more sites the order
              of travel can't be assumed, so only legs trucks actually drove
              today are drawn (loaded once the job is pinned). */}
            {visibleJobs
              .filter((job) => job.sites.length === 2)
              .map((job) => (
                <SiteLane
                  key={job.id}
                  from={job.sites[0] as LiveMapSite}
                  to={job.sites[1] as LiveMapSite}
                  color={job.color}
                  isDimmed={!!selectedTruck && !selectedTruckJobs.includes(job)}
                />
              ))}
            {observedLanes.map(({ job, from, to, count }) => (
              <SiteLane
                key={`${job.id}-${from.id}-${to.id}`}
                from={from}
                to={to}
                color={job.color}
                count={count}
                isDimmed={!!selectedTruck && !selectedTruckJobs.includes(job)}
              />
            ))}

            {/* Site geofences, drawn like react-app: the hand-drawn polygon when
              the site has one, otherwise the radius circle, plus the inner circle */}
            {geofenceSites.map((site) =>
              site.polygon ? (
                <Polygon
                  key={`geofence-${site.id}`}
                  path={site.polygon}
                  options={SITE_POLYGON_OPTIONS}
                />
              ) : (
                <Circle
                  key={`geofence-${site.id}`}
                  center={site}
                  radius={site.radiusMeters}
                  options={SITE_CIRCLE_OPTIONS}
                />
              )
            )}
            {geofenceSites.map((site) =>
              site.innerRadiusMeters ? (
                <Circle
                  key={`inner-geofence-${site.id}`}
                  center={site}
                  radius={site.innerRadiusMeters}
                  options={SITE_INNER_CIRCLE_OPTIONS}
                />
              ) : null
            )}

            {/* Short recent tail behind each truck shows the lane it is running */}
            {visibleTrucks.map(({ operator, location, job }) =>
              operator.uid === selectedTruckUid ? null : (
                <TruckTail
                  key={`tail-${operator.uid}`}
                  points={location.recent}
                  color={job.color}
                  isDimmed={!!selectedTruck}
                />
              )
            )}

            {/* Past days: the route each truck of the pinned jobs drove */}
            {pastRoutes.map(({ job, operator, path }) => (
              <PastRoute
                key={`route-${operator.uid}`}
                path={path}
                color={job.color}
              />
            ))}

            {!!selectedTruck && selectedTrailPath.length > 1 && (
              <SelectedTrail
                path={selectedTrailPath}
                color={selectedTruck.job.color}
              />
            )}

            {siteMarkers.map((data) => (
              <SiteMarker
                key={data.site.id}
                data={data}
                variant={siteMarkerVariant}
                truckCount={trucksOnSiteBySiteId[data.site.id] || 0}
                isHighlighted={cameFrom?.site.id === data.site.id}
                isSelected={data.site.id === selectedSiteId}
                onClick={() => onSelectSite(data.site.id)}
              />
            ))}

            {visibleTrucks.map(({ operator, location, job }) => (
              <TruckMarker
                key={operator.uid}
                operator={operator}
                location={location}
                profile={profiles[operator.uid]}
                jobColor={job.color}
                isSelected={operator.uid === selectedTruckUid}
                nowMs={nowMs}
                onClick={() => onSelectTruck(operator.uid)}
              />
            ))}

            {!!selectedTruck && !!selectedActivity && (
              <InfoWindow
                key={selectedTruck.operator.uid}
                position={selectedTruck.location.latest}
                offsetY={TRUCK_MARKER_SIZE_PX / 2 + 4}
                onClose={onClearTruck}
              >
                <TruckDetails
                  operator={selectedTruck.operator}
                  location={selectedTruck.location}
                  profile={profiles[selectedTruck.operator.uid]}
                  activity={selectedActivity}
                  cameFrom={cameFrom}
                  jobsById={jobsById}
                  pinnedJobIds={pinnedJobIds}
                  onTogglePin={togglePinnedJob}
                  isStale={getIsLocationStale(selectedTruck.location, nowMs)}
                  nowMs={nowMs}
                />
              </InfoWindow>
            )}

            {!!selectedSite && (
              <InfoWindow
                key={`site-${selectedSite.site.id}`}
                position={selectedSite.site}
                offsetY={
                  siteMarkerVariant === 'tag'
                    ? SITE_TAG_HEIGHT_PX
                    : SITE_PIN_HEIGHT_PX
                }
                onClose={onClearSite}
              >
                <SiteDetails
                  data={selectedSiteAllJobs || selectedSite}
                  truckCount={
                    isLiveDay
                      ? trucksOnSiteBySiteId[selectedSite.site.id] || 0
                      : null
                  }
                  pinnedJobIds={pinnedJobIds}
                  onTogglePin={togglePinnedJob}
                  onSetJobsPinned={setJobsPinned}
                />
              </InfoWindow>
            )}
          </GoogleMap>

          <div className="pointer-events-none absolute top-2 left-2 flex flex-col items-start gap-2">
            {!isPaneOpen && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm bg-base-100 pointer-events-auto gap-1 shadow"
                  onClick={() => setIsPaneOpen(true)}
                >
                  <QueueListIcon className="size-4" />
                  {`Jobs (${jobs.length})`}
                </button>
                {!!pinnedJobIds.length && (
                  <PinnedSummary
                    pinnedCount={pinnedJobIds.length}
                    totalCount={jobs.length}
                    onShowAll={clearPinnedJobs}
                    className="bg-base-100 pointer-events-auto rounded-lg py-1 pr-1 pl-2 shadow"
                  />
                )}
              </div>
            )}
            <div className="bg-base-100/95 pointer-events-auto flex items-center gap-3 rounded-lg px-3 py-1.5 text-xs shadow">
              {dayKind === 'today' ? (
                <span>
                  <span className="font-bold">{liveTruckCount}</span>
                  {` live ${liveTruckCount === 1 ? 'truck' : 'trucks'}`}
                  {visibleTrucks.length > liveTruckCount &&
                    ` · ${visibleTrucks.length - liveTruckCount} no recent signal`}
                </span>
              ) : dayKind === 'past' ? (
                <span>
                  <span className="font-bold">{dayLabel}</span>
                  {pinnedJobIds.length
                    ? ` · routes driven by ${pastRoutes.length} ${pastRoutes.length === 1 ? 'truck' : 'trucks'}`
                    : ' · pin jobs to see the routes their trucks drove'}
                </span>
              ) : (
                <span>
                  <span className="font-bold">{dayLabel}</span>
                  {' · planned jobs'}
                  {!pinnedJobIds.length &&
                    (recentJobs.isLoading
                      ? ` · loading sites from the last ${RECENT_JOBS_DAYS} days...`
                      : ` · faded pins: sites used in the last ${RECENT_JOBS_DAYS} days`)}
                  {recentJobs.isCapped &&
                    !pinnedJobIds.length &&
                    ` (latest ${RECENT_JOBS_LIMIT.toLocaleString()} jobs)`}
                </span>
              )}
              {isSimulatorAvailable && isLiveDay && (
                <label className="flex cursor-pointer items-center gap-1.5">
                  <input
                    type="checkbox"
                    className="toggle toggle-xs toggle-info"
                    checked={isSimulationOn}
                    onChange={() => setIsSimulationOn(!isSimulationOn)}
                  />
                  {'Simulate GPS'}
                </label>
              )}
              {isSimulating && (
                <span className="badge badge-info badge-sm font-bold">
                  {'SIMULATED'}
                </span>
              )}
            </div>
            <MapLegend showTrucks={isLiveDay} />
          </div>

          {isLiveDay &&
            !isLoading &&
            !!jobs.length &&
            !visibleTrucks.length && (
              <div className="pointer-events-none absolute inset-x-0 bottom-6 flex justify-center px-4">
                <div className="alert bg-base-100 pointer-events-auto max-w-lg text-sm shadow">
                  {isSimulatorAvailable && !isSimulating
                    ? 'No trucks on these jobs have reported a location today. Turn on "Simulate GPS" to preview the map with simulated trucks.'
                    : 'No trucks on these jobs have reported a location today.'}
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

/**
 * Strip across the top of the map with the day it shows and a button to close
 * the map. Pinned jobs and filters show in the active filters bar above it.
 * @param {object} props
 * @param {string} props.title - e.g. "Live map · Thu, Oct 15".
 * @param {() => void} props.onClose
 * @returns {React.JSX.Element}
 */
function MapHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="border-base-200 flex items-center gap-2 border-b px-3 py-1.5 text-sm">
      <MapIcon className="text-accent size-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate font-bold">{title}</span>
      <button
        type="button"
        className="btn btn-ghost btn-sm btn-square"
        onClick={onClose}
        title="Close the map"
        aria-label="Close the map"
      >
        <XMarkIcon className="size-5" />
      </button>
    </div>
  );
}

// Site roles shown in the legend, in reading order
const siteRoleKinds = ['load', 'dump', 'both'] as const;

/**
 * Small key explaining the site role icons and, on today's map, the truck symbols.
 * @param {object} props
 * @param {boolean} props.showTrucks
 * @returns {React.JSX.Element}
 */
function MapLegend({ showTrucks }: { showTrucks: boolean }) {
  const swatch = 'block size-4 rounded-full border-2 border-black bg-white';
  return (
    <div className="bg-base-100/95 pointer-events-auto hidden max-w-xl flex-wrap items-center gap-x-3 gap-y-1 rounded-lg px-3 py-1.5 text-xs shadow md:flex">
      {siteRoleKinds.map((roleKind) => (
        <span key={roleKind} className="flex items-center gap-1">
          <SiteRoleIcon roleKind={roleKind} />
          {SITE_ROLE_LABELS[roleKind]}
        </span>
      ))}
      {showTrucks && <TruckLegend swatch={swatch} />}
    </div>
  );
}

/**
 * Truck symbol part of the legend.
 * @param {object} props
 * @param {string} props.swatch - Classes for the sample marker.
 * @returns {React.JSX.Element}
 */
function TruckLegend({ swatch }: { swatch: string }) {
  return (
    <>
      <span className="flex items-center gap-1">
        <span className="relative">
          <svg
            viewBox="0 0 12 10"
            className="absolute -top-1.5 left-1/2 h-1.5 w-2 -translate-x-1/2 fill-black"
            aria-hidden="true"
          >
            <path d="M6 0 L12 10 L0 10 Z" />
          </svg>
          <span className={swatch} />
        </span>
        {'Moving (notch shows direction)'}
      </span>
      <span className="flex items-center gap-1">
        <span className={swatch} />
        {'Stopped'}
      </span>
      <span className="flex items-center gap-1">
        <span className={clsx(swatch, 'opacity-50')} />
        {'No signal 5+ min'}
      </span>
      <span className="flex items-center gap-1">
        <span
          className={clsx(swatch, 'opacity-50')}
          style={{ borderColor: OPERATOR_MARKER_COLORS.strokeNotTracking }}
        />
        {'Location sharing off'}
      </span>
    </>
  );
}

const pastRouteOptionsCache: Record<string, google.maps.PolylineOptions> = {};

/**
 * Style for a truck's route on a past day, with arrows showing the direction
 * driven. Cached per color so lines aren't restyled on every render.
 * @param {string} color - Job color.
 * @returns {google.maps.PolylineOptions}
 */
function pastRouteOptions(color: string): google.maps.PolylineOptions {
  pastRouteOptionsCache[color] = pastRouteOptionsCache[color] || {
    strokeColor: color,
    strokeOpacity: 0.6,
    strokeWeight: 3,
    zIndex: 8,
    icons: [
      {
        icon: {
          path: google.maps.SymbolPath.FORWARD_OPEN_ARROW,
          scale: 2,
          strokeColor: color,
          strokeOpacity: 0.9,
        },
        offset: '60px',
        repeat: '160px',
      },
    ],
  };
  return pastRouteOptionsCache[color];
}

// Site geofence styles from react-app (components/Map/Markers/SiteCircle.js and
// SitePolygon.js): COLORS.secondary circle, COLORS.primary inner circle and a
// COLORS.secondaryDark polygon
const SITE_CIRCLE_OPTIONS: google.maps.CircleOptions = {
  strokeWeight: 0,
  fillColor: '#0098cd',
  fillOpacity: 0.3,
  zIndex: 1,
};
const SITE_INNER_CIRCLE_OPTIONS: google.maps.CircleOptions = {
  strokeWeight: 0,
  fillColor: '#b21f28',
  fillOpacity: 0.3,
  zIndex: 2,
};
const SITE_POLYGON_OPTIONS: google.maps.PolygonOptions = {
  fillColor: '#004750',
  fillOpacity: 0.3,
  strokeColor: '#004750',
  strokeWeight: 2,
  zIndex: 1,
};

const selectedTrailOptionsCache: Record<string, google.maps.PolylineOptions> =
  {};

/**
 * Style for the selected truck's route today. Cached per color so the line
 * isn't restyled on every render (e.g. the 15-second clock tick).
 * @param {string} color
 * @returns {google.maps.PolylineOptions}
 */
function selectedTrailOptions(color: string): google.maps.PolylineOptions {
  selectedTrailOptionsCache[color] = selectedTrailOptionsCache[color] || {
    strokeColor: color,
    strokeOpacity: 0.9,
    strokeWeight: 4,
    zIndex: 30,
    icons: [
      {
        icon: {
          path: google.maps.SymbolPath.FORWARD_OPEN_ARROW,
          scale: 2.5,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
        offset: '40px',
        repeat: '120px',
      },
    ],
  };
  return selectedTrailOptionsCache[color];
}

/**
 * The route one truck drove on a past day. The style is looked up here rather
 * than in LiveMapView's render, because it uses `google.maps` and the map only
 * renders its children once the Maps script has loaded.
 * @param {object} props
 * @param {LatLngLiteral[]} props.path
 * @param {string} props.color - Job color.
 * @returns {React.JSX.Element}
 */
function PastRoute({ path, color }: { path: LatLngLiteral[]; color: string }) {
  return <Polyline path={path} options={pastRouteOptions(color)} />;
}

/**
 * The selected truck's route today. Like PastRoute, its style is looked up
 * only once the Maps script has loaded.
 * @param {object} props
 * @param {LatLngLiteral[]} props.path
 * @param {string} props.color - Job color.
 * @returns {React.JSX.Element}
 */
function SelectedTrail({
  path,
  color,
}: {
  path: LatLngLiteral[];
  color: string;
}) {
  return <Polyline path={path} options={selectedTrailOptions(color)} />;
}

/**
 * Dashed lane from one site to another with a direction arrow. Observed legs put
 * the arrow 40% of the way along so legs driven in both directions show two
 * separate arrows, and get thicker the more often they were driven.
 * @param {object} props
 * @param {LiveMapSite} props.from
 * @param {LiveMapSite} props.to
 * @param {string} props.color - Job color.
 * @param {number} [props.count] - Times the leg was driven today (observed legs only).
 * @param {boolean} props.isDimmed
 * @returns {React.JSX.Element}
 */
function SiteLane({
  from,
  to,
  color,
  count,
  isDimmed,
}: {
  from: LiveMapSite;
  to: LiveMapSite;
  color: string;
  count?: number;
  isDimmed: boolean;
}) {
  const path = useMemo(
    () => [
      { lat: from.lat, lng: from.lng },
      { lat: to.lat, lng: to.lng },
    ],
    [from.lat, from.lng, to.lat, to.lng]
  );
  const isObserved = count !== undefined;
  const dashScale = 3 + Math.min(Math.max((count || 1) - 1, 0), 3) * 0.5;
  const options = useMemo<google.maps.PolylineOptions>(
    () => ({
      geodesic: true,
      strokeOpacity: 0,
      zIndex: 5,
      icons: [
        {
          icon: {
            path: 'M 0,-1 0,1',
            strokeColor: color,
            strokeOpacity: isDimmed ? 0.2 : 0.7,
            scale: dashScale,
          },
          offset: '0',
          repeat: '14px',
        },
        {
          icon: {
            path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            strokeColor: color,
            fillColor: color,
            fillOpacity: isDimmed ? 0.2 : 0.9,
            strokeOpacity: isDimmed ? 0.2 : 0.9,
            scale: 3,
          },
          offset: isObserved ? '40%' : '50%',
        },
      ],
    }),
    [color, isDimmed, dashScale, isObserved]
  );
  return <Polyline path={path} options={options} />;
}

/**
 * Faint line through a truck's most recent fixes.
 * @returns {React.JSX.Element | null}
 */
function TruckTail({
  points,
  color,
  isDimmed,
}: {
  points: LatLngLiteral[];
  color: string;
  isDimmed: boolean;
}) {
  const path = useMemo(
    () => points.map(({ lat, lng }) => ({ lat, lng })),
    [points]
  );
  const options = useMemo<google.maps.PolylineOptions>(
    () => ({
      strokeColor: color,
      strokeOpacity: isDimmed ? 0.15 : 0.45,
      strokeWeight: 3,
      zIndex: 10,
    }),
    [color, isDimmed]
  );
  return path.length > 1 ? <Polyline path={path} options={options} /> : null;
}

/**
 * Fit the map to a set of points whenever `frameKey` changes.
 * @returns {null}
 */
function FitBounds({
  points,
  frameKey,
}: {
  points: LatLngLiteral[];
  frameKey: string;
}) {
  const { map } = useGoogleMap();
  useEffect(() => {
    // "0" means nothing has asked for a frame yet
    if (frameKey === '0' || !points.length) return;
    const bounds = new google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    // Extra top padding keeps sites clear of the overlay controls
    map.fitBounds(bounds, { top: 110, right: 60, bottom: 60, left: 60 });
    // Don't zoom all the way in on a single point
    const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
      if ((map.getZoom() || 0) > 15) map.setZoom(15);
    });
    return () => listener.remove();
  }, [frameKey]);
  return null;
}

/**
 * Zoom the map out one level, keeping its center, each time `request` changes.
 * Tracks the last handled request so React's development double-run of effects
 * doesn't zoom out twice.
 * @param {object} props
 * @param {number} props.request - Incremented to ask for another zoom-out.
 * @returns {null}
 */
function ZoomOutOneLevel({ request }: { request: number }) {
  const { map } = useGoogleMap();
  const handledRequestRef = useRef(request);
  useEffect(() => {
    if (request === handledRequestRef.current) return;
    handledRequestRef.current = request;
    map.setZoom(Math.max((map.getZoom() || 0) - 1, 0));
  }, [request]);
  return null;
}

/**
 * Pan to a truck when it gets selected, without changing the zoom.
 * @returns {null}
 */
function PanToSelectedTruck({
  position,
  uid,
}: {
  position: LatLngLiteral | null;
  uid: string | null;
}) {
  const { map } = useGoogleMap();
  useEffect(() => {
    if (position) map.panTo(position);
  }, [uid]);
  return null;
}
